import type { ComponentType, ReactNode } from 'react';
import type { ItineraryView } from '../model/itinerary-view';
import { ArrowDownUp, Calendar, Layers, Lock, LockOpen } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import css from './itinerary-view-tabs.module.scss';

const chip = pickCss(css, css.chip);

type IconCmp = ComponentType<{ size?: number }>;

type TabSpec = {
  view: ItineraryView;
  label: string;
  icon?: IconCmp;
};

// View toggles (URL-driven). "Chapters" is NOT here — it is the
// manager-modal opener button, not a view mode. Default view (no
// `?view=`) renders the day list; Calendar is the only real toggle.
const MAIN_TABS: ReadonlyArray<TabSpec> = [
  { view: 'calendar', label: 'Calendar', icon: Calendar },
];

type ItineraryViewTabsViewProps = {
  active: ItineraryView;
  isLocked: boolean;
  // Read-only historical-version view: the Lock chip is a mutation and is
  // hidden entirely. View switching and Collapse all are read-only, so stay.
  isReadOnly?: boolean;
  lockDisabled: boolean;
  allCollapsed: boolean;
  // Chapter-editor modal open state — drives the active style on the
  // "Chapters" button. Modal lives at the page level.
  isChapterEditorOpen: boolean;
  onOpenChapterEditor: () => void;
  onSelectView: (view: ItineraryView) => void;
  onToggleLock: () => void;
  onCollapseAll: () => void;
  // Slot rendered inside the first chip group, after the Calendar
  // tab. The page wires `<ChapterSelector>` here so the filter pill
  // sits in the same row as View / Chapters / Calendar rather than
  // wrapping to its own line below.
  chapterSelectorSlot?: ReactNode;
  // "View" single-day filter pill (`<DayViewSelector>`), same row.
  daySelectorSlot?: ReactNode;
  // "Booking status" filter pill (`<BookingStatusFilter>`). Sits in the RIGHT
  // chip group, before Collapse all — it reviews what is already on screen
  // rather than choosing what the planner loads, so it reads with the
  // view-level controls, not with the day/chapter narrowing pills.
  bookingStatusSlot?: ReactNode;
  // "Removed services" filter pill (`<RemovedServicesFilter>`), beside
  // Booking status — also a review lens over the day list.
  removedServicesSlot?: ReactNode;
};

// Presentation. No tRPC, no router, no toast. Renders the chip row +
// lock toggle; emits intent via the on* callbacks.
export const ItineraryViewTabsView = ({
  active,
  isLocked,
  isReadOnly = false,
  lockDisabled,
  allCollapsed,
  isChapterEditorOpen,
  onOpenChapterEditor,
  onSelectView,
  onToggleLock,
  onCollapseAll,
  chapterSelectorSlot,
  daySelectorSlot,
  bookingStatusSlot,
  removedServicesSlot,
}: ItineraryViewTabsViewProps) => (
  <div className={css.root}>
    <div className={css.group}>
      <button
        className={chip({ isActive: active !== 'calendar' })}
        onClick={() => onSelectView('chapters')}
        type="button"
      >
        <span className={css.chipIcon}>
          <ArrowDownUp size={12} />
        </span>
        Planner
      </button>
      <button
        className={chip({ isActive: isChapterEditorOpen })}
        onClick={onOpenChapterEditor}
        type="button"
      >
        <span className={css.chipIcon}>
          <Layers size={12} />
        </span>
        Chapters
      </button>
      {MAIN_TABS.map(({ view, label, icon: Icon }) => (
        <button
          className={chip({ isActive: active === view })}
          key={view}
          onClick={() => onSelectView(view)}
          type="button"
        >
          {Icon && (
            <span className={css.chipIcon}>
              <Icon size={12} />
            </span>
          )}
          {label}
        </button>
      ))}
      {daySelectorSlot}
      {chapterSelectorSlot}
    </div>
    <div className={css.group}>
      {removedServicesSlot}
      {bookingStatusSlot}
      <button className={chip()} onClick={onCollapseAll} type="button">
        {allCollapsed ? 'Expand all' : 'Collapse all'}
      </button>
      <button
        aria-pressed={isLocked}
        className={chip({ isActive: isLocked })}
        disabled={isReadOnly || lockDisabled}
        onClick={onToggleLock}
        title={
          isReadOnly
            ? 'Viewing a previous version — editing is disabled'
            : isLocked
              ? 'Itinerary is locked. Day-structure mutations (add / remove day in the middle) are blocked. Click to unlock.'
              : 'Itinerary is unlocked. Click to lock — middle-day adds and any day removal will be rejected.'
        }
        type="button"
      >
        <span className={css.chipIcon}>
          {isLocked ? <Lock size={12} /> : <LockOpen size={12} />}
        </span>
        {isLocked ? 'Locked' : 'Lock'}
      </button>
    </div>
  </div>
);
